import { useEffect, useMemo, useRef } from 'react';
import FormatAlignLeft from '@mui/icons-material/FormatAlignLeft';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import {
  LinkBubbleMenu,
  LinkBubbleMenuHandler,
  MenuButtonBlockquote,
  MenuButtonBold,
  MenuButtonBulletedList,
  MenuButtonCode,
  MenuButtonCodeBlock,
  MenuButtonEditLink,
  MenuButtonHighlightColor,
  MenuButtonHorizontalRule,
  MenuButtonIndent,
  MenuButtonItalic,
  MenuButtonOrderedList,
  MenuButtonRedo,
  MenuButtonRemoveFormatting,
  MenuButtonStrikethrough,
  MenuButtonTextColor,
  MenuButtonUnderline,
  MenuButtonUndo,
  MenuButtonUnindent,
  MenuControlsContainer,
  MenuDivider,
  MenuSelectHeading,
  MenuSelectTextAlign,
  RichTextEditor,
  type RichTextEditorRef,
} from 'mui-tiptap';
import { useLocalization } from '@/hooks/useLocalization';
import { invitationVariablePasteExtension } from './invitationVariablePasteExtension';
import type { InsertInvitationTemplateVariable } from '../constants/invitationTemplateVariables';

const EMPTY_DOC = '<p></p>';

type InvitationMessageRichTextEditorProps = {
  value: string;
  onChange: (html: string) => void;
  onReady?: (insertVariable: InsertInvitationTemplateVariable) => void;
  error?: boolean;
};

/**
 * MUI-themed rich text editor for invitation HTML templates.
 * Syncs HTML with the parent via `onUpdate`; external `value` updates use `setContent` without emitting duplicate updates.
 */
const InvitationMessageRichTextEditor = ({
  value,
  onChange,
  onReady,
  error = false,
}: InvitationMessageRichTextEditorProps) => {
  const { t } = useLocalization();
  const rteRef = useRef<RichTextEditorRef>(null);
  const placeholderText = t(
    'bulkInvite.messageEditorPlaceholder',
    'Use *|salutation|*, *|groupname|*, and other variables from the list above.',
  );

  const extensions = useMemo(
    () => [
      StarterKit.configure({ heading: { levels: [1, 2, 3, 4, 5, 6] } }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
      }),
      LinkBubbleMenuHandler,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Placeholder.configure({ placeholder: placeholderText }),
      invitationVariablePasteExtension,
    ],
    [placeholderText],
  );

  useEffect(() => {
    const insertVariable: InsertInvitationTemplateVariable = (token) => {
      const editor = rteRef.current?.editor;
      if (!editor || editor.isDestroyed) return;
      editor.chain().focus().insertContent(token).run();
    };
    onReady?.(insertVariable);
  }, [onReady]);

  useEffect(() => {
    const editor = rteRef.current?.editor;
    if (!editor || editor.isDestroyed) return;
    const current = editor.getHTML();
    if (current === value) return;
    editor.commands.setContent(value || EMPTY_DOC, { emitUpdate: false });
  }, [value]);

  const initialContent = value || EMPTY_DOC;

  return (
    <RichTextEditor
      ref={rteRef}
      extensions={extensions}
      content={initialContent}
      editorDependencies={[placeholderText]}
      onUpdate={({ editor }) => {
        onChange(editor.getHTML());
      }}
      shouldRerenderOnTransaction
      renderControls={(editor) =>
        editor ? (
          <MenuControlsContainer>
            <MenuSelectHeading
              sx={{
                '& .MuiTiptap-MenuSelectHeading-selectInput': {
                  width: 'auto',
                },
              }}
            />
            <MenuDivider />
            <MenuButtonBold />
            <MenuButtonItalic />
            <MenuButtonUnderline />
            <MenuButtonStrikethrough />
            <MenuDivider />
            <MenuButtonTextColor />
            <MenuButtonHighlightColor />
            <MenuDivider />
            <MenuButtonOrderedList />
            <MenuButtonBulletedList />
            <MenuButtonIndent />
            <MenuButtonUnindent />
            <MenuDivider />
            <MenuSelectTextAlign
              aria-label={t(
                'bulkInvite.textAlignOptions',
                'Text alignment options',
              )}
              tooltipTitle={t(
                'bulkInvite.textAlignTooltip',
                'Change text alignment',
              )}
              emptyLabel={
                <FormatAlignLeft
                  sx={{ fontSize: 18 }}
                  aria-hidden
                />
              }
            />
            <MenuDivider />
            <MenuButtonEditLink />
            <MenuButtonBlockquote />
            <MenuButtonCode />
            <MenuButtonCodeBlock />
            <MenuButtonHorizontalRule />
            <MenuDivider />
            <MenuButtonUndo />
            <MenuButtonRedo />
            <MenuDivider />
            <MenuButtonRemoveFormatting />
          </MenuControlsContainer>
        ) : null
      }
      children={(editor) => (editor ? <LinkBubbleMenu /> : null)}
      RichTextFieldProps={{ variant: 'outlined', error }}
    />
  );
};

export default InvitationMessageRichTextEditor;
